import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Editorial "Condiciones Comerciales" — a compact in-page entry that opens a
// premium, fully-scrollable modal with the entire commercial agreement.
// The visible section is intentionally minimal (eyebrow · title · intro ·
// link). All 25 clauses live inside the modal.

const FULL_CLAUSES = [
  {
    n: "01",
    title: "Relación comercial",
    paragraphs: [
      "La contratación de un proyecto establece una relación comercial entre La Michi Agencia y el cliente conforme a la propuesta aceptada, estas Condiciones Comerciales y cualquier acuerdo adicional confirmado por escrito.",
      "En caso de contradicción, prevalecerán las condiciones específicas indicadas en la propuesta aceptada.",
    ],
  },
  {
    n: "02",
    title: "Alcance del proyecto",
    paragraphs: [
      "Antes de comenzar, se definirá el alcance del proyecto, incluyendo las páginas, funcionalidades, integraciones, entregables y servicios incluidos.",
      "Cualquier elemento que no figure expresamente dentro del alcance inicial podrá considerarse trabajo adicional y presupuestarse por separado.",
      "Nuestro objetivo es evitar sorpresas para ambas partes y mantener el proyecto organizado desde el principio.",
    ],
  },
  {
    n: "03",
    title: "Comunicación",
    paragraphs: [
      "Trabajamos con un único punto de contacto designado por el cliente para centralizar comentarios, decisiones, contenidos y aprobaciones.",
      "La comunicación se realizará principalmente por escrito a través del canal acordado para mantener un registro claro de las decisiones tomadas durante el proyecto.",
      "El cliente se compromete a facilitar respuestas, contenidos y aprobaciones dentro de plazos razonables para evitar retrasos.",
    ],
  },
  {
    n: "04",
    title: "Confidencialidad",
    paragraphs: [
      "Toda la información, documentación, credenciales, estrategias y materiales compartidos durante el proyecto serán tratados de forma estrictamente confidencial.",
      "La información se utilizará únicamente para la prestación del servicio contratado y no será compartida con terceros, salvo cuando resulte estrictamente necesario para completar el proyecto o exista una obligación legal.",
    ],
  },
  {
    n: "05",
    title: "Protección de datos",
    paragraphs: [
      "Los datos personales serán tratados conforme al Reglamento General de Protección de Datos, la Ley Orgánica de Protección de Datos Personales y garantía de los derechos digitales y cualquier otra normativa española aplicable.",
      "Solo recopilaremos los datos necesarios para gestionar la relación comercial, prestar el servicio, emitir facturas y mantener la comunicación relacionada con el proyecto.",
      "Cuando resulte necesario utilizar proveedores externos para la prestación del servicio, dichos proveedores podrán actuar como encargados del tratamiento conforme a sus propias condiciones y políticas de privacidad.",
      "La información completa sobre el tratamiento de datos estará disponible en la Política de Privacidad de La Michi Agencia.",
    ],
  },
  {
    n: "06",
    title: "Dominio, hosting y accesos",
    paragraphs: [
      "El dominio, el servicio de hosting y las cuentas asociadas al proyecto pertenecen en todo momento al cliente.",
      "Cuando sea necesario para completar el proyecto, el cliente será responsable de proporcionar acceso temporal a las plataformas, cuentas o servicios requeridos.",
      "Esto puede incluir, entre otros:",
    ],
    list: [
      "Dominio y configuración DNS",
      "Servicio de hosting",
      "Gestor de contenidos o web existente",
      "Correo electrónico utilizado para formularios",
      "Plataformas de analítica",
      "Google Search Console",
      "Google Analytics",
      "Herramientas de reservas",
      "Plataformas de pago",
      "Otros servicios vinculados al proyecto",
    ],
    trailing: [
      "La Michi Agencia utilizará dichos accesos exclusivamente para realizar las tareas necesarias dentro del alcance acordado.",
      "El cliente podrá revocar estos accesos una vez finalizado el proyecto.",
      "La Michi Agencia no solicitará la propiedad de las cuentas del cliente ni registrará el dominio o los servicios esenciales a su nombre, salvo solicitud expresa y acuerdo escrito.",
    ],
  },
  {
    n: "07",
    title: "Pagos",
    paragraphs: [
      "Salvo que la propuesta indique condiciones diferentes, el proyecto requiere un anticipo del 50 % para reservar la agenda y comenzar los trabajos.",
      "El 50 % restante deberá abonarse antes de la publicación, entrega definitiva o transferencia del proyecto.",
      "La entrega final, publicación o transferencia de accesos podrá aplazarse hasta que se haya recibido el pago completo.",
      "Los servicios adicionales no incluidos en el alcance original podrán requerir un presupuesto y pago independientes.",
    ],
  },
  {
    n: "08",
    title: "Cancelación del proyecto",
    paragraphs: [
      "El cliente podrá cancelar el proyecto comunicándolo por escrito.",
    ],
    subsections: [
      {
        title: "Cancelación durante las primeras 48 horas",
        body: "Si el cliente cancela durante las primeras 48 horas desde la aceptación del proyecto, se reembolsará el anticipo abonado, descontando 100 € en concepto de planificación, gestión administrativa y reserva de agenda.",
      },
      {
        title: "Cancelación a partir del tercer día",
        body: "A partir del tercer día natural, el anticipo abonado dejará de ser reembolsable.",
      },
      {
        title: "Cancelación después de siete días",
        body: "Una vez transcurridos siete días naturales desde la aceptación del proyecto, el importe total de 699 € será exigible, incluso si el cliente decide no continuar. Esta condición también podrá aplicarse antes de dicho plazo cuando el proyecto se haya completado o el desarrollo se encuentre sustancialmente avanzado.",
      },
    ],
    trailing: [
      "Las cantidades correspondientes a servicios adicionales previamente aprobados también serán exigibles cuando dichos trabajos ya hayan comenzado.",
    ],
  },
  {
    n: "09",
    title: "Contenidos y materiales del cliente",
    paragraphs: [
      "El cliente será responsable de facilitar los textos, imágenes, logotipos, datos de contacto, precios, información comercial y demás materiales necesarios para el proyecto.",
      "El cliente garantiza que dispone de los derechos, autorizaciones y licencias necesarias para utilizar todos los materiales proporcionados.",
      "La Michi Agencia no será responsable de reclamaciones, sanciones o conflictos relacionados con contenidos facilitados por el cliente sin los derechos correspondientes.",
    ],
  },
  {
    n: "10",
    title: "Exactitud de la información",
    paragraphs: [
      "El cliente será responsable de comprobar que la información publicada en la web sea correcta, completa y actualizada.",
      "Antes de la publicación, el cliente tendrá la oportunidad de revisar el proyecto y comunicar posibles errores.",
      "Los cambios solicitados después de la aprobación final como consecuencia de información incorrecta, incompleta o desactualizada proporcionada por el cliente podrán presupuestarse por separado.",
    ],
  },
  {
    n: "11",
    title: "Plazos de entrega",
    paragraphs: [
      "Los plazos indicados son estimaciones y comenzarán cuando La Michi Agencia haya recibido:",
    ],
    list: [
      "El anticipo correspondiente",
      "Los contenidos necesarios",
      "Los accesos requeridos",
      "Las decisiones pendientes",
      "La información necesaria para iniciar el proyecto",
    ],
    trailing: [
      "Los retrasos en la entrega de contenidos, accesos, comentarios o aprobaciones podrán desplazar la fecha estimada de entrega.",
      "La Michi Agencia informará al cliente cuando una demora pueda afectar de forma significativa al calendario.",
    ],
  },
  {
    n: "12",
    title: "Revisiones",
    paragraphs: [
      "El proyecto incluye las rondas de revisión indicadas en la propuesta aceptada.",
      "Una ronda de revisión consiste en un conjunto consolidado de comentarios remitidos por el punto de contacto designado por el cliente.",
      "Las revisiones no incluyen la sustitución completa del concepto aprobado, la incorporación de nuevas páginas, nuevas funcionalidades o cambios sustanciales en el alcance.",
      "Las revisiones adicionales o modificaciones fuera del alcance podrán presupuestarse de manera independiente.",
    ],
  },
  {
    n: "13",
    title: "Aprobaciones",
    paragraphs: [
      "Las aprobaciones importantes deberán realizarse por escrito.",
      "Cuando La Michi Agencia solicite la aprobación de una fase, el cliente deberá revisar el contenido y comunicar sus comentarios dentro del plazo indicado.",
      "Si el cliente no responde después de los recordatorios correspondientes y dentro de un plazo razonable, la fase podrá considerarse aprobada para permitir el avance del proyecto.",
    ],
  },
  {
    n: "14",
    title: "Proyecto en pausa",
    paragraphs: [
      "Si el cliente deja de responder, no facilita los materiales necesarios o no realiza las aprobaciones correspondientes, La Michi Agencia podrá pausar el proyecto.",
      "La reanudación quedará sujeta a la disponibilidad de agenda existente en ese momento.",
      "Si la pausa se prolonga y requiere reorganizar, reconstruir o revisar trabajos ya realizados, podrán aplicarse costes adicionales previa comunicación al cliente.",
    ],
  },
  {
    n: "15",
    title: "Tecnología y compatibilidad",
    paragraphs: [
      "La Michi Agencia se compromete a entregar una web funcional, responsive y desarrollada conforme al alcance acordado.",
      "La web se optimizará para los navegadores y dispositivos modernos de uso habitual en el momento del desarrollo.",
      "No podemos garantizar compatibilidad indefinida con navegadores obsoletos, dispositivos antiguos o futuras actualizaciones realizadas por terceros.",
    ],
  },
  {
    n: "16",
    title: "Servicios de terceros",
    paragraphs: [
      "El proyecto podrá utilizar servicios proporcionados por terceros, como hosting, dominios, sistemas de correo, herramientas de analítica, formularios, plataformas de reservas, pasarelas de pago, APIs o integraciones externas.",
      "La Michi Agencia no controla la disponibilidad, el funcionamiento, los precios, las políticas ni las actualizaciones de dichos proveedores.",
      "No seremos responsables de errores, interrupciones, cambios, pérdidas de servicio o modificaciones provocadas por plataformas externas.",
      "Cuando una incidencia externa requiera trabajo adicional, dicho trabajo podrá presupuestarse por separado.",
    ],
  },
  {
    n: "17",
    title: "Propiedad intelectual",
    paragraphs: [
      "Una vez recibido el pago completo, la web y los entregables creados específicamente para el proyecto pasarán a ser propiedad del cliente.",
      "Esta transferencia no incluye:",
    ],
    list: [
      "Herramientas de terceros",
      "Tipografías sujetas a licencia",
      "Fotografías de stock",
      "Plugins",
      "Plantillas",
      "Software",
      "Código",
      "Bibliotecas",
      "Recursos con licencias independientes",
    ],
    trailing: [
      "Estos elementos continuarán sujetos a las condiciones de sus respectivos proveedores.",
      "La Michi Agencia podrá conservar conocimientos, procesos, sistemas, componentes reutilizables y metodologías generales empleados durante el proyecto.",
    ],
  },
  {
    n: "18",
    title: "Portfolio",
    paragraphs: [
      "Salvo acuerdo escrito en contrario, La Michi Agencia podrá mostrar el proyecto terminado en su portfolio, redes sociales, presentaciones comerciales o materiales promocionales.",
      "Esto podrá incluir capturas de pantalla, enlaces, imágenes y una descripción general del trabajo realizado.",
      "No se divulgará información confidencial del cliente.",
    ],
  },
  {
    n: "19",
    title: "Limitación de responsabilidad",
    paragraphs: [
      "La Michi Agencia se compromete a entregar una landing page funcional, responsive y desarrollada conforme al alcance acordado.",
      "Nuestro trabajo consiste en diseñar y desarrollar la página web contratada.",
      "No garantizamos resultados comerciales específicos derivados de su publicación o utilización, incluyendo:",
    ],
    list: [
      "Posicionamiento en Google",
      "Incremento de ventas",
      "Incremento del número de clientes",
      "Generación de leads",
      "Tasas de conversión",
      "Facturación",
      "Rentabilidad",
      "Resultados de campañas publicitarias",
    ],
    trailing: [
      "El rendimiento de una web depende de múltiples factores ajenos a La Michi Agencia, como el mercado, la competencia, la propuesta comercial, los precios, la reputación del negocio, la inversión publicitaria, el tráfico, la estrategia de ventas y el comportamiento de los usuarios.",
      "La Michi Agencia tampoco será responsable de decisiones comerciales tomadas por el cliente basándose en los resultados de la web.",
    ],
  },
  {
    n: "20",
    title: "Mantenimiento y cambios posteriores",
    paragraphs: [
      "Salvo que la propuesta indique lo contrario, el proyecto no incluye mantenimiento continuo, actualizaciones indefinidas ni soporte permanente.",
      "Las pequeñas mejoras posteriores incluidas en la propuesta estarán sujetas al periodo, cantidad y condiciones allí indicadas.",
      "Las modificaciones, nuevas páginas, integraciones, correcciones provocadas por terceros o trabajos solicitados después de la entrega podrán presupuestarse de forma independiente.",
    ],
  },
  {
    n: "21",
    title: "Comportamiento profesional",
    paragraphs: [
      "Ambas partes se comprometen a mantener una comunicación respetuosa, profesional y de buena fe durante todo el proyecto.",
      "La Michi Agencia podrá pausar o finalizar la colaboración cuando se produzcan comportamientos abusivos, ofensivos, discriminatorios, intimidatorios o amenazas hacia cualquier persona implicada en el proyecto.",
      "En dichos casos, los importes correspondientes al trabajo ya realizado seguirán siendo exigibles.",
    ],
  },
  {
    n: "22",
    title: "Fuerza mayor",
    paragraphs: [
      "Ninguna de las partes será responsable de retrasos o incumplimientos provocados por circunstancias razonablemente fuera de su control.",
      "Esto podrá incluir fallos generalizados de proveedores, interrupciones de servicios, desastres naturales, conflictos, enfermedades graves, cambios normativos u otras circunstancias extraordinarias.",
      "Cuando sea posible, ambas partes colaborarán para reorganizar el calendario de forma razonable.",
    ],
  },
  {
    n: "23",
    title: "Sin permanencia",
    paragraphs: [
      "La contratación de un proyecto no implica un compromiso de permanencia ni obliga al cliente a contratar servicios posteriores.",
      "Una vez finalizado el proyecto y recibido el pago completo, el cliente podrá gestionar su web de forma independiente o solicitar nuevos servicios cuando los necesite.",
    ],
  },
  {
    n: "24",
    title: "Legislación aplicable",
    paragraphs: [
      "Estas Condiciones Comerciales se regirán por la legislación española.",
      "Cualquier conflicto se intentará resolver inicialmente de forma amistosa y de buena fe.",
      "Cuando no sea posible alcanzar un acuerdo, se aplicará la jurisdicción legalmente competente.",
    ],
  },
  {
    n: "25",
    title: "Aceptación",
    paragraphs: [
      "La aceptación de la propuesta, el pago del anticipo o la solicitud expresa de inicio de los trabajos implican la aceptación de estas Condiciones Comerciales.",
      "Cada proyecto quedará vinculado a la versión de las condiciones vigente en la fecha de aceptación.",
    ],
  },
];

// ---------- Modal ----------
export default function ConditionsModal({ open, onClose, triggerRef }) {
  const closeBtnRef = useRef(null);
  const scrollAreaRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Body scroll lock + Lenis pause + focus
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      if (window.__lenis) window.__lenis.stop();
      // Focus close button after animation
      const t = setTimeout(() => closeBtnRef.current?.focus(), 220);
      return () => clearTimeout(t);
    }
    document.body.style.overflow = "";
    if (window.__lenis) window.__lenis.start();
    // Restore focus to trigger when closed
    if (triggerRef?.current) triggerRef.current.focus();
    return undefined;
  }, [open, triggerRef]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9990] flex items-center justify-center p-3 md:p-6 lg:p-8"
          style={{ overflow: "hidden" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22, ease: [0.76, 0, 0.24, 1] }}
        >
          {/* Backdrop */}
          <motion.div
            aria-hidden="true"
            data-testid="cc-modal-backdrop"
            onClick={onClose}
            className="absolute inset-0 bg-black/80 cursor-pointer"
            style={{ backdropFilter: "blur(6px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Panel */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cc-modal-title"
            data-testid="cc-modal"
            className="relative w-full max-w-[900px] max-h-[95vh] md:max-h-[90vh] flex flex-col section-dark border border-white/10"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.24, ease: [0.76, 0, 0.24, 1] }}
          >
            {/* Sticky header */}
            <div className="shrink-0 flex items-start justify-between gap-6 px-6 md:px-10 pt-7 md:pt-9 pb-5 border-b border-white/10 bg-inherit">
              <div>
                <div className="font-sans text-[10px] tracking-[0.32em] uppercase opacity-50 mb-3">
                  — La letra pequeña
                </div>
                <h2
                  id="cc-modal-title"
                  className="font-display text-[26px] md:text-3xl lg:text-4xl leading-[1.02] tighter pb-1"
                >
                  Condiciones <span className="font-italic-display" style={{ color: "#FF6B2C" }}>Comerciales</span>
                </h2>
                <div className="mt-2 font-sans text-[11px] tracking-[0.22em] uppercase opacity-50">
                  La Michi Agencia · Versión 1.0
                </div>
              </div>
              <button
                ref={closeBtnRef}
                onClick={onClose}
                data-testid="cc-modal-close"
                aria-label="Cerrar"
                className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full border border-white/30 hover:border-white/80 transition-colors focus:outline-none focus:ring-1 focus:ring-white/60"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M2 2 L 12 12 M 12 2 L 2 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Scrollable body — flex-1 + min-h-0 lets it shrink; overscroll-contain
                prevents scroll chaining; touch scrolling enabled for iOS.
                data-lenis-prevent tells Lenis to ignore wheel/touch events
                inside this container so native scroll works normally. */}
            <div
              ref={scrollAreaRef}
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 md:px-10 py-8 md:py-12"
              style={{ WebkitOverflowScrolling: "touch" }}
              data-testid="cc-modal-scroll"
              data-lenis-prevent
              tabIndex={0}
            >
              <div className="space-y-14 md:space-y-16 max-w-2xl mx-auto">
                {FULL_CLAUSES.map((c) => (
                  <article key={c.n} data-testid={`cc-clause-${c.n}`}>
                    <div className="flex items-baseline gap-3 mb-4">
                      <span className="font-sans text-[10px] tracking-[0.24em] uppercase opacity-45">
                        {c.n}
                      </span>
                      <div className="flex-1 h-px bg-white/10" />
                    </div>
                    <h3 className="font-display text-xl md:text-2xl leading-[1.15] tighter mb-5">
                      {c.title}
                    </h3>
                    <div className="font-sans text-[15px] leading-[1.8] opacity-80 space-y-4">
                      {c.paragraphs?.map((p, i) => (
                        <p key={`p-${i}`}>{p}</p>
                      ))}
                      {c.list && (
                        <ul className="pl-4 space-y-1 list-disc marker:text-white/40">
                          {c.list.map((li, i) => (
                            <li key={`li-${i}`}>{li}</li>
                          ))}
                        </ul>
                      )}
                      {c.subsections?.map((s, i) => (
                        <div key={`sub-${i}`} className="mt-4">
                          <div className="font-sans text-[13px] uppercase tracking-[0.14em] opacity-75 mb-1">
                            {s.title}
                          </div>
                          <p>{s.body}</p>
                        </div>
                      ))}
                      {c.trailing?.map((p, i) => (
                        <p key={`t-${i}`}>{p}</p>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 px-6 md:px-10 py-5 border-t border-white/10">
              <div className="font-sans text-[11px] tracking-[0.22em] uppercase opacity-50">
                Condiciones Comerciales · Versión 1.0
              </div>
              <button
                onClick={onClose}
                data-testid="cc-modal-close-bottom"
                className="font-sans text-[11px] tracking-[0.24em] uppercase opacity-75 hover:opacity-100 transition-opacity"
              >
                Cerrar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------- Main section removed (2026-07-30). Modal is now consumed
// directly by the Footer to avoid a redundant summary block. ----------
